import {ReportableError, ReportableWarning} from '@wix/document-manager-utils'
import {
    basedOnSignatureNS,
    CustomGetter,
    DalValueChangeCallback,
    Getter,
    OpenTransaction,
    TransactionEvent,
    TransactionEvents
} from './documentManagerDal'
import {createStore, DalJsStore, DalStore, DmStore} from './store'
import {createSnapshotChain, SnapshotDal} from './snapshot/snapshotDal'
import type {
    DalValue,
    DropCommitContext,
    Pointer,
    Tags,
    Transaction,
    Namespace,
    SimpleDalAPI,
    FragmentPointer
} from '@wix/document-services-types'
import type {CoreConfig} from '../documentManagerCore'
import type {
    DocumentDataTypes,
    FilterGetter,
    IdGenerator,
    IndexKey,
    KeyValPredicate,
    Null,
    NullUndefined,
    PostSetOperation,
    PostTransactionOperation,
    PostTransactionSideEffect,
    ValidationWhitelistCheck,
    BaselineBlacklistCheck
} from '../types'
import type {BaseLineNestedObject, ValidateValue} from './validation/dalValidation'
import {map_findBy, map_pickBy} from '../utils/pickBy'
import type {IndexedValues, QueryIndex, QueryNamespace, ValueToIndexIds} from './queryIndex/queryIndex'
import {
    getInnerPath,
    getInnerPointer,
    getInnerValue,
    getPointer,
    hasInnerPath,
    stripInnerPath
} from '../utils/pointerUtils'
import _ from 'lodash'
import {
    createImmutableProxy,
    createImmutableProxyForMap,
    createImmutableProxyForMapOfMaps,
    deepClone
} from '@wix/wix-immutable-proxy'
import type {SnapshotList, Store} from './snapshot/snapshotList'
import type {TagManager} from './snapshot/tagManager'
import type {EventEmitter} from 'events'
import {isConflicting as isConflictingValue} from './signatures/isConflicting'
import {deepCompare} from '../utils/deepCompare'
import {deepCompareIgnoreSignature} from '../utils/deepCompareIgnoreSignature'
import type {FragmentDalJsStore, FragmentStoreMap} from './fragmentStore'
import type {DalSchema} from './schema/dalSchema'
import {PAGE_FRAGMENT_TYPE} from './fragment/fragmentDalManager'

const EMPTY_MAP = new Map()
const DAL_READ_VALUES = 'DAL_READ_VALUES'
const DAL_READ_INDEX = 'DAL_READ_INDEX'

/**
 * document manager DAL
 */
export interface DAL extends SimpleDalAPI {
    readonly queryFilterGetters: Record<string, FilterGetter>
    readonly tagManager: TagManager
    readonly registrar: {
        registerFilter(name: string, filter: ValueToIndexIds): void
        registerValidator(name: string, validate: ValidateValue): void
        registerRebaseValidator(name: string, validate: ValidateValue): void
        registerValidationWhitelistCheck(whitelistCheck: ValidationWhitelistCheck): void
        registerPostTransactionOperation(name: string, operation: PostTransactionOperation): void
        registerBaselineBlacklistCheck(check: BaselineBlacklistCheck): void
        registerForChangesCallback(callback: DalValueChangeCallback): void
        unregisterForChangesCallback(callback: DalValueChangeCallback): void
    }

    // for debug / test
    readonly _store: DmStore
    readonly _queryIndex: QueryIndex
    readonly _tentativeStore: DmStore
    readonly _snapshots: SnapshotList
    readonly _currentTransaction: OpenTransaction
    _getMergedOpenTransactionStoreAsJson?(): DalJsStore
    _getMergedTentativeStoreAsJson?(): DalJsStore
    _getMergedApprovedStoreAsJson?(): DalJsStore
    _getAllFragmentsOpenTransactionStoreAsJson?(): FragmentDalJsStore
    _getAllFragmentsApprovedStoreAsJson?(): FragmentDalJsStore

    _getBaseLineViolations(): BaseLineNestedObject
    _getApprovedStoreAsJson(): DalJsStore
    _getTentativeStoreAsJson(): DalJsStore
    _getCommittedStoreAsJson(): DalJsStore
    _getMergedStoreAsJson(): DalJsStore
    _getFragmentsMergedStoreAsJson?(): FragmentDalJsStore
    _getAllTags(): Record<string, string[]>

    /** mark items as approved and move them from tentative store to the store */
    approve(pid: string): void
    reject(pid: string): void

    commitTransaction(committer?: string, skipValidations?: boolean): SnapshotDal
    safeCommitTransactionWithErrorReport(additionalErrorTags: Record<string, any>, committer?: string): SnapshotDal
    find(namespace: string, indexKey: IndexKey, predicate: KeyValPredicate, fragmentType?: string): any
    getIndexed(indexKey: IndexKey, fragmentType?: string): IndexedValues
    getIndexKeys(indexName: string, fragmentType?: string): string[]
    get(pointer: Pointer): DalValue
    getRegisteredTypes(): DocumentDataTypes
    getWithPath(pointer: Pointer, innerPath: string | string[]): any
    has(pointer: Pointer): boolean
    isDirty(pointer: Pointer): boolean
    query(
        namespace: string,
        indexKey: IndexKey,
        optionalPredicate?: KeyValPredicate,
        fragmentType?: string
    ): Record<string, DalValue>
    queryKeys(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate, fragmentType?: string): any
    getIndexPointers(indexKey: IndexKey, namespace?: string, fragmentType?: string): Pointer[]
    rebase(change: Store, position: string, label?: string): SnapshotDal
    rebaseForeignChange(change: Store, position: string, correlationId?: string): SnapshotDal
    remove(pointer: Pointer): void
    set(pointer: Pointer, value: DalValue): void
    setIfChanged(pointer: Pointer, value: DalValue): void

    /** Modify a dal value using the given function
     *
     * This is syntactic sugar, instead of:
     * ```
     * const v1 = dal.get(pointer)
     * const v2 = doSomething(v1)
     * dal.set(pointer, v2)
     * ```
     * You can use: `dal.modify(pointer, doSomething)`
     */
    modify<A, B = A>(pointer: Pointer, f: (a: A) => B): void
    touch(pointer: Pointer): void
    getLastSnapshot(): SnapshotDal | null
    takeSnapshot(tag: string): number
    takeLastApprovedSnapshot(tag: string): void
    validate(tags?: Record<string, any>): void
    validatePendingCommit(tags?: Record<string, any>): void
    createValidationBaseline(): void
    recreateValidationBaseline(): void
    sign(
        rootPointer: Pointer,
        value: DalValue,
        signOver?: Null<SnapshotDal>
    ): {value: DalValue; basedOnSignature: NullUndefined<string>}
    getBasedOnSignatureId(namespace?: string, id?: string): string
    getBasedOnSignaturePointer(pointer: Pointer): Pointer
    getBasedOnSignature(pointer: Pointer): NullUndefined<string>
    setBasedOnSignature(pointer: Pointer, signature: NullUndefined<string>): void
    getTentativeAndAcceptedAsTransaction(): Transaction
    getCurrentOpenTransaction(): Transaction
    mergeToApprovedStore(changes: Store, id?: string): SnapshotDal
    getLastApprovedSnapshot(): SnapshotDal
    dropUncommittedTransaction(reason?: string, e?: any, context?: DropCommitContext): void
    enableCommits(): void
    disableCommits(): void
    withoutCustomGetters(func: Function): any
    readonly schema: DalSchema
    hasSignature(pointer: Pointer): boolean
    getAllItemsInNamespace(namespace: Namespace): Pointer[]
    getItemsInNamespace(fragmentType: string, namespace: Namespace): FragmentPointer[]
    getNamespacesInFragment(fragmentType: string): Namespace[]
    enableShouldNotifyAboutReads(): void
}

export interface CreateArgs {
    coreConfig: CoreConfig
    postSetOperations: PostSetOperation[]
    dmTypes: DocumentDataTypes
    customGetters: Record<string, CustomGetter>
    eventEmitter: EventEmitter
    initialStore?: DalStore | FragmentStoreMap
}

export const getPointersByNamespaceKeys = (keys: string[], namespace: string, fragmentType?: string): Pointer[] =>
    keys.map(id => getPointer(id, namespace, fragmentType === PAGE_FRAGMENT_TYPE ? {} : {fragmentType}))

const pointersInNamespace = (
    namespace: QueryNamespace | undefined,
    pointerType: string,
    fragmentType?: string
): Pointer[] => (namespace ? getPointersByNamespaceKeys([...namespace.keys()], pointerType, fragmentType) : [])

// As part of the major wix wide refactor known as "the builder project"
// Some editors will wrap our pointer.
// To help them out - we throw an exception if they accidentally passed it on to us
const isEditorPointer = (pointer: Pointer & {documentPointer?: any}): boolean => pointer.documentPointer

const validatePointer = (pointer: Pointer & {documentPointer?: any}): void => {
    if (isEditorPointer(pointer)) {
        throw new ReportableError({
            errorType: 'editorPointer',
            message: 'Editor pointer detected, please unwrap the pointer before using it',
            extras: {pointer}
        })
    }
}

class CannotApproveError extends ReportableError {
    constructor(tx: SnapshotDal | null, id: string, args: any, txCompareIds: SnapshotDal | null) {
        super({
            errorType: 'cannotApproveError',
            message: `Cannot approve, ${id} is not a pending transaction`,
            extras: {
                id,
                ...args,
                unlinked: !tx,
                foundByCompareIds: !!txCompareIds,
                content: tx?.isFragmentSnapshot()
                    ? [...(tx?.getAllFragmentsStore()?.entries() ?? [])].map(([key, store]) => ({
                          [key]: _.keys(store.asJson())
                      }))
                    : _.keys(tx?.getStore().asJson())
            }
        })
        Object.setPrototypeOf(this, CannotApproveError.prototype)
    }
}

export abstract class BaseDal {
    protected abstract coreConfig: CoreConfig
    private __queryIndex?: QueryIndex
    private _currentOpenTransaction?: OpenTransaction
    private __tentativeStore?: DmStore
    private __store?: DmStore
    abstract tagManager: TagManager
    private _validator?: any
    abstract rebaseValidator: any
    private _commitsEnabled?: boolean
    protected abstract dmTypes: DocumentDataTypes
    abstract queryFilterGetters: Record<string, FilterGetter>
    protected abstract customGetters: Record<string, CustomGetter>
    private _postTransactionOperations?: Record<string, PostTransactionOperation>
    protected abstract eventEmitter: EventEmitter
    protected shouldNotifyAboutReads = false
    private _updateCallbacks?: DalValueChangeCallback[]
    protected abstract postSetOperations: PostSetOperation[]

    private _createOpenTxId?: IdGenerator
    private _createSignature?: IdGenerator

    private __rebase?(change: Store, position: string, event: TransactionEvent, id: string): SnapshotDal
    private _commitTransaction?(committer?: string, skipValidations?: boolean): SnapshotDal
    private _removeConflicts?(): void
    private __getMergedStoreAsJson?(includeCurrentTransaction: boolean): DalJsStore

    protected constructor(
        protected snapshots: SnapshotList,
        protected lastApproved: Null<SnapshotDal>,
        protected customGettersEnabled: boolean
    ) {}

    protected validateTransactionStoreSize(store: DmStore, tags?: Tags) {
        const storeActionsAmount = store.size()
        if (storeActionsAmount > this.coreConfig.transactionActionsLimit) {
            const err = new ReportableWarning({
                errorType: 'exceedsMaxActionsPerTransaction',
                message: `Exceeds actions reached ${storeActionsAmount}`,
                extras: {tags}
            })
            this.coreConfig.logger.captureError(err)
            if (this.coreConfig.experimentInstance.isOpen('dm_failOnExceedsMaxActionsPerTransaction')) {
                throw err
            }
        }
    }

    safeCommitTransactionWithErrorReport(
        additionalErrorTags: Record<string, any>,
        commiter?: string | undefined
    ): SnapshotDal {
        try {
            return this.commitTransaction(commiter)
        } catch (error: any) {
            const reportableError = new ReportableWarning(error)
            reportableError.tags ??= {}
            _.assign(reportableError.tags, additionalErrorTags)
            this.coreConfig.logger.captureError(reportableError)
            return this.commitTransaction(commiter, true)
        }
    }
    find(namespace: string, indexKey: IndexKey, predicate: KeyValPredicate) {
        this.verifyQueryParams(namespace)

        const namespaces = this.queryIndex.getIndexedValues(indexKey)
        const queryResult = namespaces.get(namespace)
        const findResult = map_findBy(queryResult, predicate)

        return createImmutableProxy(findResult)
    }
    getIndexed(indexKey: IndexKey): IndexedValues {
        const namespaces = this.queryIndex.getIndexedValues(indexKey)
        if (this.shouldNotifyAboutReads) {
            this.eventEmitter.emit(DAL_READ_INDEX, {namespacesKeys: Array.from(namespaces.keys()), indexKey})
        }
        return createImmutableProxyForMapOfMaps(namespaces ?? EMPTY_MAP) as IndexedValues
    }
    getIndexKeys(indexName: string): string[] {
        return this.queryIndex.getIndexKeys(indexName)
    }
    get(pointer: Pointer) {
        return createImmutableProxy(this.getRawValue(pointer))
    }
    getWithPath(pointer: Pointer, innerPath: string | string[]) {
        return this.get(getInnerPointer(pointer, innerPath))
    }
    has(pointer: Pointer): boolean {
        return !_.isNil(this.getRawValue(pointer))
    }
    isDirty(pointer: Pointer): boolean {
        return this.currentOpenTransaction.store.has(pointer)
    }
    query(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate | undefined): Record<string, any> {
        const result = this.queryRaw(namespace, indexKey, optionalPredicate)

        return createImmutableProxyForMap(result ?? EMPTY_MAP)
    }
    queryKeys(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate | undefined) {
        const result = this.queryRaw(namespace, indexKey, optionalPredicate)
        return result ? [...result.keys()] : []
    }
    /**
     * Returns all the pointers in the index referred to by the specified index key and optionally only in the given namespace
     * @param indexKey
     * @param namespace
     * @param fragmentType
     * @returns An array of the pointers in the specified index
     */
    getIndexPointers(indexKey: IndexKey, namespace?: string | undefined, fragmentType?: string): Pointer[] {
        const namespaces = this.queryIndex.getIndexedValues(indexKey)
        let pointers: Pointer[]
        if (namespace) {
            const namespaceValues = namespaces.get(namespace)
            pointers = pointersInNamespace(namespaceValues, namespace, fragmentType)
        } else {
            pointers = [...namespaces.entries()].flatMap(([ns, namespaceValues]) =>
                pointersInNamespace(namespaceValues, ns)
            )
        }
        if (this.shouldNotifyAboutReads) {
            this.eventEmitter.emit(DAL_READ_VALUES, pointers)
        }
        return pointers
    }
    rebase(change: DmStore, position: string, label?: string) {
        return this._rebase(
            change,
            position,
            TransactionEvents.LOCAL_TRANSACTION_APPROVED,
            this.snapshots.generateId(label)
        )
    }
    /**
     * Rebase the given snapshot and mark the new snapshot as foreign
     * @param change
     * @param position
     * @param correlationId
     */
    rebaseForeignChange(change: Store, position: string, correlationId?: string) {
        const id = correlationId ?? this.snapshots.generateId('unknown-foreign')
        const newSnapshot = this._rebase(change, position, TransactionEvents.TRANSACTION_BY_OTHER, id)
        newSnapshot.setAsForeign()
        return newSnapshot
    }
    remove(pointer: Pointer): void {
        const innerPath = getInnerPath(pointer)
        const rootPointer = stripInnerPath(pointer)
        let newValue
        if (innerPath.length > 0) {
            newValue = deepClone(this.getRawValue(rootPointer))
            _.unset(newValue, innerPath)
        }
        this.signAndUpdateTransactionAndIndex(rootPointer, newValue)
    }
    _set(pointer: Pointer, value: DalValue): void {
        validatePointer(pointer)
        const innerPath = getInnerPath(pointer)
        const rootPointer = stripInnerPath(pointer)
        let newValue = deepClone(value)
        if (innerPath.length > 0) {
            const rootValue = deepClone(this.getRawValue(rootPointer)) || {}
            newValue = _.setWith(rootValue, innerPath, newValue, Object)
        }
        this.signAndUpdateTransactionAndIndex(rootPointer, newValue)
    }
    set(pointer: Pointer, value: any): void {
        this.setIfChanged(pointer, value)
    }
    setIfChanged(pointer: Pointer, value: any): void {
        if (this.isChanged(pointer, value)) {
            this._set(pointer, value)
        }
    }
    modify<A, B = A>(pointer: Pointer, f: (a: A) => B): void {
        return this.set(pointer, f(this.get(pointer)))
    }
    touch(pointer: Pointer): void {
        return this._set(pointer, this.getterForSetAndTouch(pointer))
    }
    getLastSnapshot(): SnapshotDal | null {
        return this.snapshots.last()
    }
    takeSnapshot(tag: string): number {
        const snapshot = this.commitTransaction('takeSnapshot')
        return this.tagManager.addSnapshot(tag, snapshot)
    }
    takeLastApprovedSnapshot(tag: string): void {
        this.tagManager.addSnapshot(tag, this.getLastApprovedSnapshot())
    }
    validate(tags?: Record<string, any>) {
        const tentativeAndAccepted = this.getMergedStore(false)
        this.validator.validateStore(tentativeAndAccepted, false, tags)
    }
    validatePendingCommit(tags?: Record<string, any> | undefined): void {
        this.validateTransactionStoreSize(this.currentOpenTransaction.store, tags)
        return this.validator.validateStore(this.currentOpenTransaction.store, true, tags)
    }
    createValidationBaseline() {
        const tentativeAndAccepted = this.getMergedStore(false)
        this.validator.createBaseline(tentativeAndAccepted)
    }
    recreateValidationBaseline() {
        const tentativeAndAccepted = this.getMergedStore(false)
        this.validator.recreateBaseline(tentativeAndAccepted)
    }
    sign(
        rootPointer: Pointer,
        value: any,
        signOver?: Null<SnapshotDal> | undefined
    ): {value: any; basedOnSignature: NullUndefined<string>} {
        let basedOnSignature
        if (value && this.hasSignature(rootPointer)) {
            const previousValue = signOver ? signOver.getValue(rootPointer) : this.getCommittedValue(rootPointer)
            basedOnSignature = previousValue ? previousValue.metaData?.sig ?? null : undefined
            value.metaData = Object.assign(value.metaData || {}, {
                sig: this.createSignature()
            })
        }
        return {value, basedOnSignature}
    }
    getBasedOnSignatureId(namespace?: string | undefined, id?: string | undefined): string {
        return `${namespace}$${id}`
    }
    getBasedOnSignaturePointer(pointer: Pointer): Pointer {
        const {id, type, fragmentType} = pointer
        return getPointer(this.getBasedOnSignatureId(type, id), basedOnSignatureNS, {fragmentType})
    }
    getBasedOnSignature(pointer: Pointer): NullUndefined<string> {
        return this.get(this.getBasedOnSignaturePointer(pointer))
    }
    setBasedOnSignature(pointer: Pointer, basedOnSignature: NullUndefined<string>): void {
        this._set(this.getBasedOnSignaturePointer(pointer), basedOnSignature)
    }
    getCurrentOpenTransaction() {
        return createImmutableProxy({
            id: 'openTransaction',
            items: this.currentOpenTransaction.store.getValues()
        })
    }
    getLastApprovedSnapshot(): SnapshotDal {
        if (!this.lastApproved) {
            throw new Error('Empty DAL does not have a last approved snapshot')
        }
        return this.lastApproved
    }
    dropUncommittedTransaction(reason?: string, e?: any, context: DropCommitContext = {}) {
        if (this.currentOpenTransaction.store.isEmpty()) {
            return
        }
        const pointersToUpdate = this.currentOpenTransaction.store.keys()
        const pointerToReport = _(pointersToUpdate)
            .reject(ptr => {
                return ptr.type === 'basedOnSignature'
            })
            .map('id')
            .join(',')
        console.error('dropUncommittedTransaction', e, context)
        this.coreConfig.logger.captureError(
            new ReportableError({
                message: 'dropUncommittedTransaction',
                errorType: 'dropUncommittedTransaction',
                tags: {
                    attempt: this.currentOpenTransaction.attempt,
                    id: this.currentOpenTransaction.id
                },
                extras: {
                    reason,
                    appDefinitionId: _.get(context, ['appDefinitionId'], ''),
                    stack: e?.stack ? e.stack.slice(0, 3000) : '',
                    pointersToUpdate: pointerToReport
                }
            })
        )
        this.createNewOpenTransaction()
        this.updateIndex(pointersToUpdate)
    }
    enableCommits(): void {
        this._commitsEnabled = true
    }
    disableCommits(): void {
        this._commitsEnabled = false
    }
    withoutCustomGetters(func: Function) {
        return (...args: any[]) => {
            try {
                this.customGettersEnabled = false
                return func(...args)
            } finally {
                this.customGettersEnabled = true
            }
        }
    }
    hasSignature(pointer: Pointer): boolean {
        const idsWithSignature = this.dmTypes[pointer.type]?.idsWithSignature
        if (idsWithSignature) {
            return idsWithSignature.has(pointer.id)
        }
        return this.dmTypes[pointer.type]?.hasSignature ?? false
    }
    protected isRegisteredNamespace(type: string): boolean {
        return !!this.dmTypes[type]
    }
    getRegisteredTypes(): DocumentDataTypes {
        return this.dmTypes
    }
    protected verifyQueryParams(namespace: string): void {
        if (!this.isRegisteredNamespace(namespace)) {
            console.log(`Namespace ${namespace} is not registered`)
            throw new Error(`Namespace ${namespace} is not registered`)
        }
    }
    protected queryRaw(
        namespace: string,
        indexKey: IndexKey,
        optionalPredicate?: KeyValPredicate
    ): QueryNamespace | undefined {
        const namespaces = this.queryIndex.getIndexedValues(indexKey)
        let queryNamespace = namespaces.get(namespace)
        if (optionalPredicate && queryNamespace) {
            queryNamespace = map_pickBy(queryNamespace, optionalPredicate)
        }
        if (this.shouldNotifyAboutReads) {
            this.eventEmitter.emit(DAL_READ_INDEX, {namespacesKeys: [namespace], indexKey})
        }
        return queryNamespace
    }
    protected getFromStores(stores: DmStore[], rootPointer: Pointer): DalValue {
        const foundStore = stores.find((s: DmStore) => s.has(rootPointer))
        return foundStore ? foundStore.get(rootPointer) : undefined
    }
    protected getMergedStoreValue(pointer: Pointer): any {
        return this.getFromStores([this.currentOpenTransaction.store, this.tentativeStore, this.store], pointer)
    }
    protected getCommittedValue(pointer: Pointer): DalValue {
        return this.getFromStores([this.tentativeStore, this.store], pointer)
    }
    protected getApprovedValue(pointer: Pointer): DalValue {
        return this.getFromStores([this.store], pointer)
    }
    protected getRootValueFromCustomGetterOrStore(rootPointer: Pointer): DalValue {
        const customGetterArgument = {
            queryFilterGetters: this.queryFilterGetters,
            query: this.query,
            get: (ptr: Pointer) => createImmutableProxy(this.getMergedStoreValue(ptr))
        }
        //either using an extension defined getter or the default store
        if (this.customGettersEnabled) {
            const customGetter = this.customGetters[rootPointer.type]
            if (customGetter) {
                return customGetter(customGetterArgument, rootPointer)
            }
        }
        return this.getCommittedValue(rootPointer)
    }
    protected getRawValue(pointer: Pointer): DalValue {
        validatePointer(pointer)
        if (this.shouldNotifyAboutReads) {
            this.eventEmitter.emit(DAL_READ_VALUES, [pointer])
        }
        const transactionStore = this.currentOpenTransaction.store
        const rootPointer = stripInnerPath(pointer)
        let rootValue
        if (transactionStore.has(rootPointer)) {
            rootValue = transactionStore.get(rootPointer)
        } else {
            rootValue = this.getRootValueFromCustomGetterOrStore(rootPointer)
        }
        return getInnerValue(pointer, rootValue)
    }
    protected createNewOpenTransaction() {
        const newTransaction = {
            attempt: 0,
            id: this.createOpenTxId(),
            store: createStore()
        }
        this._currentOpenTransaction = newTransaction

        return newTransaction
    }
    protected callPostTransactionOperationsAndAccumulateAsyncSideEffects(
        transaction: Transaction
    ): null | PostTransactionSideEffect {
        const asyncSideEffects = _(this.postTransactionOperations)
            .map(cb => cb(transaction))
            .compact()
            .value() as PostTransactionSideEffect[]

        const hasSideEffects = !_.isEmpty(asyncSideEffects)
        if (hasSideEffects) {
            return async () => {
                await Promise.all(asyncSideEffects.map(p => p()))
            }
        }
        return null
    }
    report(reportStore: Store, event: TransactionEvent): void {
        if (reportStore.isEmpty()) {
            return
        }
        const transaction = {
            id: 'post-transaction-report',
            items: reportStore.getValues()
        }
        const postTransactionSideEffectsFunction =
            this.callPostTransactionOperationsAndAccumulateAsyncSideEffects(transaction)
        this.eventEmitter.emit(event, postTransactionSideEffectsFunction)
    }
    /**
     * Returns true if any of the values in the store have a signature
     * that conflicts with that of the getter value for the same pointer
     * @param transactionStore
     * @param getter
     */
    protected isConflicting(transactionStore: Store, getter: Getter): boolean {
        return transactionStore.some(
            (pointer, value) =>
                this.hasSignature(pointer) &&
                isConflictingValue(
                    value,
                    getter(pointer),
                    transactionStore.get(this.getBasedOnSignaturePointer(pointer))
                )
        )
    }
    protected isConflictingWithMergedValue(transactionStore: DmStore): boolean {
        return this.isConflicting(transactionStore, pointer => this.getMergedStoreValue(pointer))
    }
    protected isConflictingWithCommittedValue(transactionStore: DmStore): boolean {
        return this.isConflicting(transactionStore, pointer => this.getCommittedValue(pointer))
    }
    isConflictingWithApprovedValue(transactionStore: Store): boolean {
        return this.isConflicting(transactionStore, pointer => this.getApprovedValue(pointer))
    }

    /** Update the query index of the `pointer` to its value in the merged store
     *
     * The index must always reflect the overall current state of the system.
     * Externally that would be the result from `get`, internally that would be the merged store.
     *
     * In other words, it's illegal to update the index to a value other than the one in the merged store.
     * To enforce this invariant, this function accepts only a pointer, not a value. It then fetches the value
     * from the merged store.
     */
    protected updateSingleIndexValue(pointer: Pointer): void {
        const newValue = this.getMergedStoreValue(pointer)
        const previousValue = this.queryIndex.updateIndex(pointer, newValue)
        this.updateCallbacks.forEach((callback: DalValueChangeCallback) => {
            callback(pointer, previousValue, newValue)
        })
    }

    protected updateIndex(pointers: Pointer[]): void {
        pointers.forEach(pointer => this.updateSingleIndexValue(pointer))
    }
    protected runPostSetOperations(pointer: Pointer, value: DalValue) {
        try {
            _.forEach(this.postSetOperations, postSet => postSet(pointer, value))
        } catch (error) {
            this.coreConfig.logger.captureError(error as Error, {
                tags: {postSetOp: true},
                extras: {
                    pointer,
                    value
                }
            })
            throw error
        }
    }

    protected setWithSignature(pointer: Pointer, valueToSet: DalValue) {
        const {value, basedOnSignature} = this.sign(pointer, valueToSet)
        if (value && this.hasSignature(pointer)) {
            this.setBasedOnSignature(pointer, basedOnSignature)
        }
        this.currentOpenTransaction.store.set(pointer, value)
    }

    protected signAndUpdateTransactionAndIndex(rootPointer: Pointer, value: DalValue) {
        this.setWithSignature(rootPointer, value)
        this.updateSingleIndexValue(rootPointer)
        this.runPostSetOperations(rootPointer, value)
    }

    protected getterForSetAndTouch(pointer: Pointer) {
        if (this.coreConfig.experimentInstance.isOpen('dm_useCustomGettersForSetAndTouch')) {
            return this.getRawValue(pointer)
        }
        return this.getMergedStoreValue(pointer)
    }

    protected isChanged(pointer: Pointer, value: DalValue): boolean {
        const currentValue = this.getterForSetAndTouch(pointer)
        if (hasInnerPath(pointer)) {
            const currentInnerValue = getInnerValue(pointer, currentValue)
            return !deepCompare(currentInnerValue, value)
        }

        return !deepCompareIgnoreSignature(currentValue, value)
    }
    protected isSameValue(a: DalValue, b: DalValue) {
        const sigA = a?.metaData?.sig
        const sigB = b?.metaData?.sig
        return sigA || sigB ? sigA === sigB : deepCompare(a, b)
    }

    protected collectChanges(oldTentativeStore: DmStore, originalChange?: DmStore): DmStore {
        const changes = createStore()

        oldTentativeStore.forEach((pointer, otherValue) => {
            const dalValue = this.getCommittedValue(pointer)
            if (!this.isSameValue(dalValue, otherValue)) {
                changes.set(pointer, dalValue)
            }
        })

        if (originalChange) {
            originalChange.forEach((pointer, value) => {
                if (!oldTentativeStore.has(pointer)) {
                    changes.set(pointer, value)
                }
            })
        }

        return changes
    }

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    getItemsInNamespace(fragmentType: string, namespace: Namespace): FragmentPointer[] {
        throw new Error('Not implemented')
    }

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    getNamespacesInFragment(fragmentType: string): Namespace[] {
        throw new Error('Not implemented')
    }

    getMergedStore(includeCurrentTransaction: boolean): Store {
        const mergedStore = createStore()
        mergedStore.merge(this.store)
        mergedStore.merge(this.tentativeStore)
        if (includeCurrentTransaction) {
            mergedStore.merge(this.currentOpenTransaction.store)
        }

        return mergedStore
    }

    getMergedNamespace(namespace: Namespace): Record<string, DalValue> {
        const mergedNamespace = {}
        this.store.applyTypeValuesWithDelete(namespace, mergedNamespace)
        this.tentativeStore.applyTypeValuesWithDelete(namespace, mergedNamespace)
        this.currentOpenTransaction.store.applyTypeValuesWithDelete(namespace, mergedNamespace)

        return mergedNamespace
    }

    /** Run validators on the pointers that exist in `storeToValidate`
     *
     * The validators will be called only on these pointers but remember that they already
     * have access to the entire DAL (since they're part of the extensions).
     * That means that to ensure correctness this function needs to run on a valid DAL with an
     * updated index.
     * In addition, extensions that hold internal state usually update said state in postTransactionOperations.
     * If validators rely on that state and postTransactionOperations weren't called, this function may behave poorly.
     */
    protected hasRebaseValidationErrors(storeToValidate: DmStore): boolean {
        try {
            this.rebaseValidator.validateStore(storeToValidate, true, {isRebasing: true})
            return false
        } catch (e) {
            return true
        }
    }

    protected rebuildOpenStore(snaps: SnapshotDal[], fragmentType?: string): void {
        const keys = this.currentOpenTransaction.store.keys()
        this.createNewOpenTransaction()
        snaps.forEach(snap => {
            const snapStore = fragmentType ? snap.getFragmentStore(fragmentType) : snap.getStore()
            this.currentOpenTransaction.store.merge(snapStore)
            keys.push(...snapStore.keys())
        })
        this.updateIndex(keys)
    }

    protected removeTentativeSnapshotWithoutUpdatingTheIndex(snapshot: SnapshotDal): void {
        this.snapshots.remove(snapshot)
        this.tagManager.remove(snapshot)
    }

    protected resetToApprovedState(): void {
        const pointersToUpdate = _.concat(this.tentativeStore.keys(), this.currentOpenTransaction.store.keys())
        this.__tentativeStore = createStore()
        this.createNewOpenTransaction()
        this.updateIndex(pointersToUpdate)
    }

    /**
     * Build a new tentative store with all conflicting snapshots removed
     * Aggregate all the changes between the old tentative store and new one, update
     * the query index with these changes and notify listeners of the changes
     * @param {TransactionEvent} event - event type to report
     * @param {DmStore} originalChange - change of data that is not included in the tentative store
     */
    removeConflictsAndReport(event: TransactionEvent, originalChange?: DmStore) {
        const inconsistentTentativeStore = this.tentativeStore

        // remove invalidated snapshots
        this.removeConflicts()
        const changes: DmStore = this.collectChanges(inconsistentTentativeStore, originalChange)

        // notify listeners
        this.report(changes, event)
    }

    /**
     * apply function on all the snapshots from but not including fromSnapshot, up to and including toSnapshot
     * @param {SnapshotDal} from
     * @param {SnapshotDal} to
     * @param {Function} fn
     */
    protected applyOnSnapshotsRange(from: SnapshotDal, to: SnapshotDal, fn: _.ListIterator<SnapshotDal, any>) {
        const snapshotsToApply = createSnapshotChain(from, to, 'applyOnSnapshotsRange')
        _(snapshotsToApply).reverse().forEach(fn)
    }

    protected createSnapshotTrace() {
        const snapshotChain = _(createSnapshotChain(null, this.snapshots.last(), 'createSnapshotTrace'))
            .slice(-30)
            .map('id')
            .value()
        return {
            lastApproved: this.lastApproved?.id,
            snapshotChain,
            currentTags: this.tagManager._getAllTags()
        }
    }

    protected createCannotApproveError(id: string) {
        const tx = this.snapshots.findById(id)
        const txCompareIds = this.snapshots.findByIdLimitCompareIds(id, this.lastApproved!)
        return new CannotApproveError(tx, id, this.createSnapshotTrace(), txCompareIds)
    }

    protected createCannotRebaseError(position: string, id: string | undefined) {
        return new ReportableError({
            errorType: 'cannotRebaseError',
            message: `Cannot rebase, ${position} does not come before pending transaction`,
            extras: {
                position,
                id
            }
        })
    }
    protected createRebaseUnlinkedError(position: string, id: string | undefined) {
        return new ReportableError({
            errorType: 'rebaseUnlinkedError',
            message: `Cannot rebase, ${position} is unlinked`,
            extras: {
                position,
                id,
                ...this.createSnapshotTrace()
            }
        })
    }
    protected createRemovedApprovedError(position: string, id: string | undefined) {
        return new ReportableError({
            errorType: 'removedApprovedSnapshotError',
            message: 'We just removed the approved snapshot due to a conflict',
            extras: {
                position,
                id,
                ...this.createSnapshotTrace()
            }
        })
    }
    removeConflictsReportAndVerify(change: DmStore, position: string, event: TransactionEvent, id: string) {
        this.removeConflictsAndReport(event, change)
        if (!this.snapshots.findById(id)) {
            throw this.createRemovedApprovedError(position, id)
        }
    }
    protected createMergeToApprovedError(changes: DmStore, id?: string) {
        return new ReportableError({
            errorType: 'cannotMergeToApprovedError',
            message: `MergeToApprovedStore called for ${id} outside initialization flow`,
            extras: {
                id,
                ...this.createSnapshotTrace(),
                content: _.keys(changes.asJson())
            }
        })
    }
    get _snapshots() {
        return this.snapshots
    }

    get _store() {
        return this.store
    }
    get _tentativeStore() {
        return this.tentativeStore
    }

    _getApprovedStoreAsJson() {
        return this.store.asJson()
    }
    _getTentativeStoreAsJson() {
        return this.tentativeStore.asJson()
    }
    _getCommittedStoreAsJson() {
        return this.getMergedStoreAsJson(false)
    }
    _getMergedStoreAsJson() {
        return this.getMergedStoreAsJson(true)
    }

    /** @private */
    protected get queryIndex(): QueryIndex {
        if (!this.__queryIndex) {
            throw new Error('queryIndex is not implemented in this subclass.')
        }
        return this.__queryIndex
    }

    protected set queryIndex(value: QueryIndex) {
        this.__queryIndex = value
    }

    /** @private */
    protected get currentOpenTransaction(): OpenTransaction {
        if (!this._currentOpenTransaction) {
            throw new Error('currentOpenTransaction is not implemented in this subclass.')
        }
        return this._currentOpenTransaction
    }

    protected set currentOpenTransaction(value: OpenTransaction) {
        this._currentOpenTransaction = value
    }

    /** @private */
    protected get tentativeStore(): DmStore {
        if (!this.__tentativeStore) {
            throw new Error('tentativeStore is not implemented in this subclass.')
        }
        return this.__tentativeStore
    }

    protected set tentativeStore(value: DmStore) {
        this.__tentativeStore = value
    }

    /** @private */
    protected get store(): DmStore {
        if (!this.__store) {
            throw new Error('store is not implemented in this subclass.')
        }
        return this.__store
    }

    protected set store(value: DmStore) {
        this.__store = value
    }

    /** @private */
    get validator(): any {
        if (!this._validator) {
            throw new Error('validator is not implemented in this subclass.')
        }
        return this._validator
    }

    set validator(value: any) {
        this._validator = value
    }

    /** @private */
    get postTransactionOperations(): Record<string, PostTransactionOperation> {
        if (!this._postTransactionOperations) {
            throw new Error('postTransactionOperations is not implemented in this subclass.')
        }
        return this._postTransactionOperations
    }

    set postTransactionOperations(value: Record<string, PostTransactionOperation>) {
        this._postTransactionOperations = value
    }

    enableShouldNotifyAboutReads() {
        this.shouldNotifyAboutReads = true
    }

    /** @private */
    get updateCallbacks(): DalValueChangeCallback[] {
        if (!this._updateCallbacks) {
            throw new Error('updateCallbacks is not implemented in this subclass.')
        }
        return this._updateCallbacks
    }

    set updateCallbacks(value: DalValueChangeCallback[]) {
        this._updateCallbacks = value
    }

    /** @private */
    protected get createOpenTxId(): IdGenerator {
        if (!this._createOpenTxId) {
            throw new Error('createOpenTxId is not implemented in this subclass.')
        }
        return this._createOpenTxId
    }

    protected set createOpenTxId(value: IdGenerator) {
        this._createOpenTxId = value
    }

    /** @private */
    protected get createSignature(): IdGenerator {
        if (!this._createSignature) {
            throw new Error('createSignature is not implemented in this subclass.')
        }
        return this._createSignature
    }

    protected set createSignature(value: IdGenerator) {
        this._createSignature = value
    }

    /** @private */

    protected removeConflicts() {
        if (!this._removeConflicts) {
            throw new Error('removeConflicts is not implemented in this subclass.')
        }
        this._removeConflicts()
    }

    /** @private */
    protected get commitsEnabled(): boolean {
        if (_.isUndefined(this._commitsEnabled)) {
            throw new Error('commitsEnabled is not implemented in this subclass.')
        }
        return this._commitsEnabled
    }

    protected set commitsEnabled(value: boolean) {
        this._commitsEnabled = value
    }

    /** @private */
    _rebase(change: Store, position: string, event: TransactionEvent, id: string): SnapshotDal {
        if (!this.__rebase) {
            throw new Error('_rebase is not implemented in this subclass.')
        }
        return this.__rebase(change, position, event, id)
    }

    /** @private */
    protected getMergedStoreAsJson(includeCurrentTransaction: boolean): DalJsStore {
        if (!this.__getMergedStoreAsJson) {
            throw new Error('getMergedStoreAsJson is not implemented in this subclass.')
        }
        return this.__getMergedStoreAsJson(includeCurrentTransaction)
    }

    /** @private */
    commitTransaction(committer?: string, skipValidations?: boolean): SnapshotDal {
        if (!this._commitTransaction) {
            throw new Error('commitTransaction is not implemented in this subclass.')
        }
        return this._commitTransaction(committer, skipValidations)
    }

    /** @private */
    get _queryIndex() {
        return this.queryIndex
    }

    /** @private */
    get _currentTransaction() {
        return this.currentOpenTransaction
    }

    _getBaseLineViolations() {
        return this.validator.getBaseLineViolations()
    }

    _getAllTags() {
        return this.tagManager._getAllTags()
    }
}
